import apiClient, { extractData, refreshSession } from "./client";
import { TOKEN_KEY, REFRESH_TOKEN_KEY } from "./authKeys";
import { getAuthValue, removeAuthValue, setAuthValue } from "../utils/authStorage";
import { pickPrimaryBackendRole, resolveEffectiveFrontendRole } from "../utils/hrStaffHelpers";

const DEPT_CODE_TO_FRONTEND = {
  HR: "hr-dept",
  OPS: "operations",
  MKT: "marketing",
  ACC: "accounts",
  WH: "warehouse",
  IC: "internal-control",
};

export async function loginApi(identifier, password, options = {}) {
  const headers = options.platform
    ? { "x-tenant-slug": "platform" }
    : undefined;
  const body = { identifier, password };
  const tenantSlug = String(options.tenantSlug || "").trim().toLowerCase();
  if (!options.platform && tenantSlug) {
    body.tenantSlug = tenantSlug;
  }
  const response = await apiClient.post(
    "/auth/login",
    body,
    headers ? { headers } : undefined,
  );
  const data = extractData(response);

  const accessToken = data?.tokens?.accessToken;
  const refreshToken = data?.tokens?.refreshToken;

  if (accessToken) setAuthValue(TOKEN_KEY, accessToken);
  if (refreshToken) setAuthValue(REFRESH_TOKEN_KEY, refreshToken);

  return data;
}

export async function logoutApi() {
  try {
    await apiClient.post("/auth/logout");
  } catch {
    // Ignore errors on logout
  } finally {
    removeAuthValue(TOKEN_KEY);
    removeAuthValue(REFRESH_TOKEN_KEY);
  }
}

export async function changePasswordApi(currentPassword, newPassword, confirmPassword) {
  const response = await apiClient.post("/auth/change-password", {
    currentPassword,
    newPassword,
    confirmPassword: confirmPassword ?? newPassword,
  });
  return extractData(response);
}

export async function refreshTokensApi() {
  const accessToken = await refreshSession({ logoutOnAuthFailure: false });
  return {
    accessToken,
    refreshToken: getAuthValue(REFRESH_TOKEN_KEY),
  };
}

/**
 * Map backend login user to the frontend session shape.
 */
export function mapBackendUser(backendUser) {
  if (!backendUser) return null;

  const employee = backendUser.employee;
  const roles = backendUser.roles || [];

  const roleMap = {
    employee: "employee",
    manager: "manager",
    team_leader: "manager",
    supervisor: "manager",
    branch_manager: "manager",
    regional_manager: "manager",
    department_manager: "department-manager",
    hr: "hr",
    hr_staff: "hr-staff",
    gm: "gm",
    owner: "owner",
    admin: "admin",
    platform_admin: "platform-admin",
  };

  const primaryRole = pickPrimaryBackendRole(roles);
  const mappedRole = roleMap[primaryRole] || "employee";
  const departmentFrontendId =
    DEPT_CODE_TO_FRONTEND[employee?.department?.code] ||
    employee?.departmentId ||
    null;
  const effectiveRole = resolveEffectiveFrontendRole(mappedRole, {
    departmentCode: employee?.department?.code,
    departmentId: departmentFrontendId,
    backendRoles: roles,
  });

  return {
    id: backendUser.id,
    username: backendUser.username || "",
    code: employee?.employeeCode || backendUser.username?.toUpperCase() || "",
    name: employee?.fullName || backendUser.username || backendUser.email,
    email: backendUser.email,
    role: effectiveRole,
    backendRoles: roles,
    department: employee?.department?.name || "",
    departmentId: departmentFrontendId,
    departmentCode: employee?.department?.code || null,
    departmentBackendId: employee?.departmentId || null,
    branchId: employee?.branchId || null,
    branchName: employee?.branch?.name || null,
    managedBranchIds: employee?.managedBranchIds || [],
    jobTitle: employee?.jobTitle || "",
    employeeId: employee?.id || null,
    workMode: employee?.workMode || "OFFICE",
    weeklyRestDay: employee?.weeklyRestDay
      ? String(employee.weeklyRestDay).toLowerCase()
      : null,
    mustChangePassword: Boolean(backendUser.mustChangePassword),
    isResigned: Boolean(backendUser.isResigned),
    _fromBackend: true,
  };
}
